import React from 'react';
import { motion, Variants } from 'framer-motion';
import { ShoppingBag, Wrench, TreePine, Banknote } from 'lucide-react';
import { LivingInAreaData } from '../../types/livingInArea';

interface AmenitiesSectionProps {
  data: LivingInAreaData['amenitiesSnapshot'];
}

// --- Animation Variants ---
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const cardItemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

export const AmenitiesSection: React.FC<AmenitiesSectionProps> = ({ data }) => {
  if (!data) return null;

  // Refined slots with high-contrast color mapping
  const amenitySlots = [
    {
      key: 'dailyNeeds',
      title: 'Daily Essentials',
      icon: <ShoppingBag size={28} />,
      content: data.dailyNeeds,
      color: 'text-orange-900 bg-orange-100', // Deep text on light tint
      isText: true as const,
    },
    {
      key: 'localServices',
      title: 'Local Services',
      icon: <Wrench size={28} />,
      content: data.localServices,
      color: 'text-indigo-900 bg-indigo-100',
      isText: false as const,
    },
    {
      key: 'greenSpaceAccess',
      title: 'Green Spaces',
      icon: <TreePine size={28} />,
      content: data.greenSpaceAccess,
      color: 'text-green-900 bg-green-100',
      isText: true as const,
    },
  ].filter(
    (slot) =>
      slot.content &&
      (slot.isText
        ? typeof slot.content === 'string'
        : Array.isArray(slot.content) && slot.content.length > 0)
  );

  return (
    <section className="max-w-7xl mx-auto px-4 py-12 space-y-12">
      {/* Header - High Contrast Slate */}
      <div className="text-center space-y-4">
        <h2 className="text-5xl font-black text-slate-900 tracking-tight">
          Local <span className="text-indigo-600">Amenities</span>
        </h2>
        <p className="text-xl font-bold text-slate-600 max-w-2xl mx-auto">
          What's available nearby to support daily life.
        </p>
      </div>

      {/* Cards Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="grid gap-8 md:grid-cols-3"
      >
        {amenitySlots.map((slot, index) => (
          <motion.div
            key={slot.key}
            variants={cardItemVariants}
            whileHover={{ y: -8 }}
            className="rounded-[2.5rem] border-2 border-slate-200 bg-white p-8 shadow-xl transition-all duration-300 flex flex-col"
          >
            {/* Icon Container - Increased Saturation */}
            <div
              className={`mb-6 flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl shadow-sm ${slot.color}`}
            >
              {React.cloneElement(slot.icon as React.ReactElement, { strokeWidth: 2.5 })}
            </div>

            <h3 className="mb-4 text-2xl font-black text-slate-900">{slot.title}</h3>

            {/* Content - Slate 800 for maximum legibility */}
            <div className="flex-grow">
              {slot.isText && typeof slot.content === 'string' ? (
                <p className="text-lg font-bold leading-relaxed text-slate-800">{slot.content}</p>
              ) : (
                <div className="flex flex-wrap gap-2 pt-2">
                  {(slot.content as string[]).map((service, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 + i * 0.05 }}
                      className="rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-1.5 text-sm font-black text-slate-700 shadow-sm"
                    >
                      {service}
                    </motion.span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {/* Placeholder / CTA Card */}
        {amenitySlots.length < 3 && (
          <motion.div
            variants={cardItemVariants}
            className="rounded-[2.5rem] border-2 border-dashed border-slate-300 bg-slate-50 p-8 shadow-inner text-center flex flex-col items-center justify-center"
          >
            <div className="p-4 bg-slate-200 rounded-full mb-4">
              <Banknote size={36} className="text-slate-500" />
            </div>
            <p className="text-lg font-black text-slate-700">Detailed Costs & Healthcare</p>
            <p className="text-sm font-bold text-slate-500 mt-2">
              Check our dedicated 'Cost of Living' section for more local data.
            </p>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
};
